import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Pause, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HowItWorks } from "@/components/admin/HowItWorks";
import AdminShell, { Body, Section, Stat } from "@/components/admin/AdminShell";
import { Campaigns, DEFAULT_SEGMENTS } from "@/components/admin/mk/Campaigns";
import { Templates } from "@/components/admin/mk/Templates";
import { Library } from "@/components/admin/mk/Library";
import { Deliverability } from "@/components/admin/mk/Deliverability";
import { ListCleanup } from "@/components/admin/mk/ListCleanup";
import { Card } from "@/components/Common";
import { api, enc, errMsg } from "@/lib/api";
import { label, num, pct, useAdminGet, usd, when } from "@/lib/admin";
import { cn } from "@/lib/utils";

const TABS = ["overview", "library", "campaigns", "automations", "templates"];

const Overview = () => {
  const [days, setDays] = useState(30);
  const q = useAdminGet(`/admin/mk/overview?days=${days}`);
  const audit = useAdminGet("/admin/mk/audit");
  return (
    <>
      <div className="mb-4 flex gap-1">{[7, 30, 90].map((d) => <button key={d} onClick={() => setDays(d)} className={cn("rounded-full px-3 py-1 text-xs", days === d ? "bg-[#D4AF37] font-semibold text-black" : "text-zinc-400 hover:text-zinc-200")} data-testid={`mk-days-${d}`}>{d} days</button>)}</div>
      <Body q={q}>{(d) => (
        <>
          <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Stat k="Sent" v={num(d.sent)} sub={`${num(d.failed)} failed`} testid="mk-sent" /><Stat k="Delivered" v={num(d.delivered)} sub={`${pct(d.delivery_rate)} rate`} />
            <Stat k="Clicked" v={num(d.clicked)} sub={`${pct(d.click_rate)} click rate`} /><Stat k="Conversions" v={num(d.conversions)} sub={`${pct(d.conversion_rate)} rate`} />
            <Stat k="Revenue (attributed)" v={usd(d.revenue)} tone="text-emerald-300" /><Stat k="Unsubscribes" v={num(d.unsubscribes)} sub={`${num(d.suppressed)} suppressed in total`} />
            <Stat k="Live automations" v={num(d.active_sequences)} sub={`${num(d.active_enrollments)} people enrolled`} /><Stat k="Scheduled campaigns" v={num(d.scheduled_campaigns)} />
          </div>
          <p className="mb-8 text-xs text-zinc-500">Revenue counts a purchase within 7 days of clicking an email (last click). Opens aren't tracked by design, only clicks.</p>
        </>
      )}</Body>
      <Section title="Audit log" testid="mk-audit">
        <Body q={audit}>{(d) => (
          <Card className="space-y-2">
            {!(d.items || []).length && <p className="text-sm text-zinc-500">No admin actions yet.</p>}
            {(d.items || []).slice(0, 50).map((a, i) => <div key={i} className="flex flex-wrap justify-between gap-2 border-b border-[#27272A] pb-2 text-xs last:border-0"><span className="text-zinc-300">{a.admin} · {label(a.action)} · {a.target}{a.detail ? ` · ${typeof a.detail === "string" ? a.detail : JSON.stringify(a.detail)}` : ""}</span><span className="text-zinc-500">{when(a.at)}</span></div>)}
          </Card>
        )}</Body>
      </Section>
    </>
  );
};

const Automations = ({ templates }) => {
  const q = useAdminGet("/admin/mk/sequences");
  const tname = (id) => templates.find((t) => t.template_id === id)?.name || id;
  const act = async (s, what) => {
    if (what === "approve" && !window.confirm(`Turn on "${s.name}"? Only people who trigger it from now on are enrolled.`)) return;
    try { await api.post(`/admin/mk/sequences/${enc(s.sequence_id)}/${what}`); toast.success(what === "approve" ? "Automation is live" : "Automation paused"); q.reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Body q={q}>{(d) => (
      <div className="space-y-4">
        {(d.items || []).map((s) => (
          <Card key={s.sequence_id} className="space-y-3" data-testid={`mk-sequence-${s.sequence_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><div className="font-medium text-zinc-100">{s.name}</div><div className="text-xs text-zinc-500">{s.description}</div><div className="mt-1 text-[11px] text-zinc-500">Trigger: {label(s.trigger)}{s.approved_by && ` · approved by ${s.approved_by} ${when(s.approved_at)}`}</div></div>
              <div className="flex items-center gap-2">
                <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase", s.status === "active" ? "border-emerald-500/40 text-emerald-300" : s.status === "paused" ? "border-orange-500/40 text-orange-300" : "border-zinc-600 text-zinc-400")} data-testid={`mk-sequence-status-${s.sequence_id}`}>{s.status === "draft" ? "Off (draft)" : s.status}</span>
                {s.status === "active" ? <Button size="sm" variant="outline" onClick={() => act(s, "pause")} data-testid={`mk-sequence-pause-${s.sequence_id}`}><Pause className="h-3.5 w-3.5" />Pause</Button>
                  : <Button size="sm" onClick={() => act(s, "approve")} data-testid={`mk-sequence-approve-${s.sequence_id}`}><Play className="h-3.5 w-3.5" />{s.status === "paused" ? "Resume" : "Approve & go live"}</Button>}
              </div>
            </div>
            <div className="text-xs text-zinc-500">Enrolled: {num(s.enrollments?.active)} active · {num(s.enrollments?.completed)} completed · {num(s.enrollments?.stopped)} stopped</div>
            <div className="space-y-1.5">
              {(s.steps || []).map((st, i) => (
                <div key={i} className="grid gap-2 rounded-lg border border-[#27272A] bg-[#09090B] p-2.5 text-xs sm:grid-cols-[150px_1fr_auto]">
                  <span className="font-mono text-[#F3C649]">{st.label || `+${st.delay_hours}h`}</span>
                  <span className="text-zinc-300">{tname(st.template_id)}{st.alt_template_id && <span className="text-zinc-500"> (or {tname(st.alt_template_id)})</span>}</span>
                  <span className="text-zinc-500">{num(st.stats?.sent)} sent · {pct(st.stats?.click_rate)} CTR · {num(st.stats?.conversions)} conv.</span>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    )}</Body>
  );
};

export default function EmailMarketing() {
  const [sp, setSp] = useSearchParams();
  const tab = TABS.includes(sp.get("tab")) ? sp.get("tab") : "overview";
  const tq = useAdminGet("/admin/mk/templates");
  const cq = useAdminGet(tab === "campaigns" ? "/admin/mk/campaigns" : null);
  const meta = useAdminGet("/admin/mk/meta");
  const templates = tq.data?.items || [];
  const segments = meta.data?.segments || DEFAULT_SEGMENTS;
  return (
    <AdminShell title="Email Marketing" subtitle="Nothing sends without an admin approval. Every link is click-tracked; opens are not.">
      <div className="mb-5"><HowItWorks id="mk" /></div>
      <div className="mb-6 flex gap-1 overflow-x-auto border-b border-[#27272A]">
        {TABS.map((t) => <button key={t} onClick={() => setSp({ tab: t })} className={cn("-mb-px shrink-0 border-b-2 px-3 py-2 text-sm capitalize transition-colors sm:px-4", tab === t ? "border-[#D4AF37] text-[#F3C649]" : "border-transparent text-zinc-500 hover:text-zinc-200")} data-testid={`mk-tab-${t}`}>{t}</button>)}
      </div>
      {tab === "overview" && <><Deliverability /><ListCleanup /><Overview /></>}
      {tab === "library" && <Library />}
      {tab === "campaigns" && <Campaigns q={cq} templates={templates} segments={segments} />}
      {tab === "automations" && <><Automations templates={templates} /><div className="mt-10"><Library id="mk-auto-lib" title="LinkedIn & Revenue Command Center automations" kinds={["Automation", "Campaign"]} excludeMk /></div></>}
      {tab === "templates" && <><Templates q={tq} /><div className="mt-10"><Library id="mk-tpl-lib" title="LinkedIn & Revenue Command Center templates and sequences" kinds={["Sequence", "Template"]} excludeMk /></div></>}
    </AdminShell>
  );
}
